import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { CategoryPicker } from "./CategoryPicker";
import { PrefectureCitySelect } from "./PrefectureCitySelect";
import { MunicipalityData } from "../../../../models/MunicipalityData";
import { PLACES_QUERY_MAX_RESULTS_CEILING } from "../../../../models/PlacesQueryData";
import InputBox from "../../../FormElement/InputBox";
import { useCreatePlacesQuery } from "../hooks";
import { getErrorEnvelopeMessage, isQuotaExceededError } from "../utils";

import "./styles.scss";

const DEFAULT_MAX_RESULTS = "20";

export interface ICreateQueryModalProps {
  onClose: () => void;
}

export const CreateQueryModal: React.FunctionComponent<
  ICreateQueryModalProps
> = ({ onClose }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`internalToolsHomePage.gbp_places_query.form.${key}`);

  const [name, setName] = useState("");
  const [prefecture, setPrefecture] = useState<MunicipalityData | undefined>();
  const [city, setCity] = useState<MunicipalityData | undefined>();
  const [categories, setCategories] = useState<string[]>([]);
  const [maxResults, setMaxResults] = useState(DEFAULT_MAX_RESULTS);
  const [errorMessage, setErrorMessage] = useState("");

  const createMutation = useCreatePlacesQuery();

  const isMinimallyValid =
    name.trim() !== "" && !!prefecture && categories.length > 0;

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const parsedMaxResults = parseInt(maxResults, 10);

    if (!trimmedName) {
      setErrorMessage(t("validation.name_required"));
      return;
    }
    if (!prefecture) {
      setErrorMessage(t("validation.prefecture_required"));
      return;
    }
    if (categories.length === 0) {
      setErrorMessage(t("validation.categories_required"));
      return;
    }
    if (
      !Number.isInteger(parsedMaxResults) ||
      parsedMaxResults < 1 ||
      parsedMaxResults > PLACES_QUERY_MAX_RESULTS_CEILING
    ) {
      setErrorMessage(t("validation.max_results_range"));
      return;
    }

    setErrorMessage("");

    try {
      await createMutation.mutateAsync({
        name: trimmedName,
        prefecture_id: prefecture.id.toString(),
        city_id: city ? city.id.toString() : undefined,
        categories,
        max_results: parsedMaxResults,
      });

      toast.success(t("create_success"));
      onClose();
    } catch (error) {
      if (isQuotaExceededError(error)) {
        setErrorMessage(getErrorEnvelopeMessage(error) ?? t("quota_exceeded"));
      } else {
        setErrorMessage(
          getErrorEnvelopeMessage(error) ?? _t("errors.general_error")
        );
      }
    }
  };

  return (
    <div className="modal modal-default create-query-modal">
      <div className="modal-mains">
        <button
          type="button"
          className="btn-close"
          onClick={onClose}
        ></button>
        <div className="top-title">{t("title")}</div>
        <div className="create-query-modal-field">
          <div className="label-txt">{t("name_label")}</div>
          <InputBox
            value={name}
            placeholder={t("name_placeholder")}
            onChange={setName}
          />
        </div>
        <PrefectureCitySelect
          prefecture={prefecture}
          city={city}
          onChangePrefecture={setPrefecture}
          onChangeCity={setCity}
        />
        <CategoryPicker onChange={setCategories} />
        <div className="create-query-modal-field create-query-modal-max-results">
          <div className="label-txt">{t("max_results_label")}</div>
          <input
            type="number"
            min={1}
            max={PLACES_QUERY_MAX_RESULTS_CEILING}
            value={maxResults}
            onChange={(e) => setMaxResults(e.target.value)}
          />
        </div>
        {errorMessage && (
          <p className="create-query-modal-error">{errorMessage}</p>
        )}
        <div className="bottom-btns">
          <button type="button" className="btn btn-border" onClick={onClose}>
            {t("cancel")}
          </button>
          <button
            type="button"
            className="btn btn-blue"
            disabled={!isMinimallyValid || createMutation.isLoading}
            onClick={handleSubmit}
          >
            {createMutation.isLoading ? t("submitting") : t("submit")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateQueryModal;
